const DAYS_IN_PERIOD = 30;
const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;

export type HhrgDateString = string;
export type HhrgPeriodNumber = 1 | 2;

export interface HhrgPeriodDisplay {
  periodNumber: HhrgPeriodNumber;
  startDate: HhrgDateString;
  endDate: HhrgDateString;
  oasisSource?: string | null;
  oasisDate?: HhrgDateString | null;
  primaryDiagnosis?: string | null;
  admissionSource?: string | null;
  admissionTiming?: string | null;
  clinicalGroup?: string | null;
  clinicalLevel?: string | null;
  comorbidityAdjustment?: string | null;
  lupaStatus?: string | null;
  hippsCode?: string | null;
  paymentStatus?: string | null;
  coveredVisitCount?: number | null;
  lupaThreshold?: number | null;
  caseMixWeight?: string | null;
  nationalRate?: string | null;
  wageIndex?: string | null;
  wageIndexAreaCode?: string | null;
  qualityReportingStatus?: string | null;
}

export type HhrgRevenueUnavailableReason =
  | 'lupa'
  | 'pep'
  | 'outlier'
  | 'late_noa'
  | 'missing_input'
  | 'request_failed'
  | 'unsupported';

export type HhrgAvailable<T> = {
  status: 'available';
  value: T;
};

export type HhrgUnavailable<Reason extends string> = {
  status: 'unavailable';
  reason: Reason;
};

export type HhrgRevenue =
  HhrgAvailable<number> | HhrgUnavailable<HhrgRevenueUnavailableReason>;

export interface HhrgDisciplineCostInput {
  // Known values are canonical discipline codes. Unknown API values are kept
  // here so the combination can mark their cost as unavailable.
  discipline: string;
  estimatedVisits: number;
  perVisitCost: number | null;
}

export interface HhrgPeriodInput {
  display: HhrgPeriodDisplay;
  /** The billing cycle used by future period-level revenue integrations. */
  billingCycleId?: string | null;
  revenue: HhrgRevenue;
  costs: readonly HhrgDisciplineCostInput[];
}

export type HhrgCostUnavailableReason = 'missing_cost';
export type HhrgMarginUnavailableReason =
  HhrgRevenueUnavailableReason | HhrgCostUnavailableReason | 'zero_revenue';
export type HhrgProfitUnavailableReason =
  HhrgRevenueUnavailableReason | HhrgCostUnavailableReason;

export type HhrgExpense =
  HhrgAvailable<number> | HhrgUnavailable<HhrgCostUnavailableReason>;
export type HhrgProfit =
  HhrgAvailable<number> | HhrgUnavailable<HhrgProfitUnavailableReason>;
export type HhrgMargin =
  HhrgAvailable<number> | HhrgUnavailable<HhrgMarginUnavailableReason>;

export type HhrgDisciplineCostOutput = HhrgDisciplineCostInput & {
  totalExpense: HhrgExpense;
};

export type HhrgPeriodOutput = Omit<HhrgPeriodInput, 'costs'> & {
  costs: readonly HhrgDisciplineCostOutput[];
  totalExpense: HhrgExpense;
  profit: HhrgProfit;
  margin: HhrgMargin;
};

export interface HhrgWorksheetOutput {
  periods: readonly [HhrgPeriodOutput, HhrgPeriodOutput];
}

export class HhrgWorksheetValidationError extends Error {
  readonly issues: readonly string[];

  constructor(issues: readonly string[]) {
    super(`Invalid HHRG worksheet input: ${issues.join('; ')}`);
    this.name = 'HhrgWorksheetValidationError';
    this.issues = issues;
  }
}

const parseDate = (value: HhrgDateString): number | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;

  const [year, month, day] = value.split('-').map(Number);
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);

  return parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
    ? timestamp
    : null;
};

const validatePeriods = (periods: readonly HhrgPeriodInput[]): string[] => {
  const issues: string[] = [];

  if (periods.length !== 2) {
    issues.push('exactly two periods are required');
    return issues;
  }

  const timestamps = periods.map(periodInput => ({
    start: parseDate(periodInput.display.startDate),
    end: parseDate(periodInput.display.endDate),
  }));

  periods.forEach((periodInput, index) => {
    const expectedNumber = (index + 1) as HhrgPeriodNumber;
    if (periodInput.display.periodNumber !== expectedNumber) {
      issues.push(
        `period ${index + 1} must have period number ${expectedNumber}`
      );
    }

    const timestamp = timestamps[index];
    if (timestamp.start === null) {
      issues.push(`period ${index + 1} has an invalid start date`);
    }
    if (timestamp.end === null) {
      issues.push(`period ${index + 1} has an invalid end date`);
    }
    if (timestamp.start !== null && timestamp.end !== null) {
      if (
        timestamp.end - timestamp.start !==
        (DAYS_IN_PERIOD - 1) * MILLISECONDS_IN_DAY
      ) {
        issues.push(
          `period ${index + 1} must contain exactly 30 inclusive days`
        );
      }
    }
  });

  const first = timestamps[0];
  const second = timestamps[1];
  if (
    first.start !== null &&
    first.end !== null &&
    second.start !== null &&
    second.end !== null &&
    second.start !== first.end + MILLISECONDS_IN_DAY
  ) {
    issues.push('periods must be consecutive calendar periods');
  }

  return issues;
};

const validateCosts = (periods: readonly HhrgPeriodInput[]): string[] => {
  const issues: string[] = [];

  periods.forEach((periodInput, periodIndex) => {
    periodInput.costs.forEach((cost, costIndex) => {
      const label = `period ${periodIndex + 1} cost ${costIndex + 1}`;
      if (!Number.isInteger(cost.estimatedVisits) || cost.estimatedVisits < 0) {
        issues.push(
          `${label} estimated visits must be a non-negative whole number`
        );
      }
      if (
        cost.perVisitCost !== null &&
        (!Number.isFinite(cost.perVisitCost) || cost.perVisitCost < 0)
      ) {
        issues.push(
          `${label} per-visit cost must be non-negative when configured`
        );
      }
    });
  });

  return issues;
};

const validateRevenue = (periods: readonly HhrgPeriodInput[]): string[] =>
  periods.flatMap((periodInput, index) => {
    if (
      periodInput.revenue.status === 'available' &&
      (!Number.isFinite(periodInput.revenue.value) ||
        periodInput.revenue.value < 0)
    ) {
      return [
        `period ${index + 1} revenue must be non-negative when available`,
      ];
    }
    return [];
  });

export const validateHhrgWorksheetInput = (
  periods: readonly HhrgPeriodInput[]
): readonly string[] => [
  ...validatePeriods(periods),
  ...validateCosts(periods),
  ...validateRevenue(periods),
];

const combinePeriod = (periodInput: HhrgPeriodInput): HhrgPeriodOutput => {
  const costs = periodInput.costs.map(cost => ({
    ...cost,
    totalExpense:
      cost.perVisitCost === null && cost.estimatedVisits > 0
        ? ({ status: 'unavailable', reason: 'missing_cost' } as const)
        : ({
            status: 'available',
            value: cost.estimatedVisits * (cost.perVisitCost ?? 0),
          } as const),
  }));

  const totalExpense =
    costs.length === 0 ||
    costs.some(cost => cost.totalExpense.status === 'unavailable')
      ? ({ status: 'unavailable', reason: 'missing_cost' } as const)
      : {
          status: 'available' as const,
          value: costs.reduce(
            (total, cost) =>
              total +
              (cost.totalExpense.status === 'available'
                ? cost.totalExpense.value
                : 0),
            0
          ),
        };

  let profit: HhrgProfit;
  let margin: HhrgMargin;

  if (periodInput.revenue.status === 'unavailable') {
    profit = { status: 'unavailable', reason: periodInput.revenue.reason };
    margin = { status: 'unavailable', reason: periodInput.revenue.reason };
  } else if (totalExpense.status === 'unavailable') {
    profit = { status: 'unavailable', reason: 'missing_cost' };
    margin = { status: 'unavailable', reason: 'missing_cost' };
  } else {
    const profitValue = periodInput.revenue.value - totalExpense.value;
    profit = { status: 'available', value: profitValue };
    margin =
      periodInput.revenue.value > 0
        ? {
            status: 'available',
            value: (profitValue / periodInput.revenue.value) * 100,
          }
        : { status: 'unavailable', reason: 'zero_revenue' };
  }

  return {
    ...periodInput,
    costs,
    totalExpense,
    profit,
    margin,
  };
};

export const combineHhrgWorksheet = (
  periods: readonly HhrgPeriodInput[]
): HhrgWorksheetOutput => {
  const issues = validateHhrgWorksheetInput(periods);
  if (issues.length > 0) {
    throw new HhrgWorksheetValidationError(issues);
  }

  const [first, second] = periods;
  return {
    periods: [combinePeriod(first), combinePeriod(second)],
  };
};
